import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { store, useAppDispatch } from './store/store';
import { checkAuth } from './store/slices/authSlice';
import DashboardLayout from './components/DashboardLayout';
import UnifiedSearchInterface from './components/UnifiedSearchInterface';
import DynamicResultsDisplay from './components/DynamicResultsDisplay';
import DetailModal from './components/DetailModal';
import InvestigatorPage from './components/InvestigatorPage';
import DeepResearchReports from './components/DeepResearchReports';
import DeepResearchPrompts from './pages/DeepResearchPrompts';
import VendorVerification from './components/vendor/VendorVerification';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import SetPasswordPage from './pages/SetPasswordPage';
import AdminUsersPage from './pages/AdminUsersPage';

// Create a custom theme for the fraud detection system
const theme = createTheme({
  palette: {
    primary: {
      main: '#2196F3',
      light: '#64B5F6',
      dark: '#1976D2',
    },
    secondary: {
      main: '#757575',
      light: '#BDBDBD',
      dark: '#424242',
    },
    error: {
      main: '#F44336',
    },
    warning: {
      main: '#FF9800',
    },
    success: {
      main: '#4CAF50',
    },
    info: {
      main: '#2196F3',
    },
    background: {
      default: '#FAFAFA',
      paper: '#FFFFFF',
    },
    grey: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#9E9E9E',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
      },
    },
  },
});

// Auth initializer component - checks authentication on app load
const AuthInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(checkAuth());
  }, [dispatch]);

  return <>{children}</>;
};

// Standard Search Page Component
const StandardSearchPage: React.FC = () => {
  return (
    <>
      <UnifiedSearchInterface
        showTypeSelector={false}
        showHeader={true}
      />
      <DynamicResultsDisplay />
    </>
  );
};

// Main App Content with Routes
const AppContent: React.FC = () => {
  return (
    <AuthInitializer>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/invite/:token" element={<SetPasswordPage />} />

          {/* Protected Routes */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <Routes>
                    {/* Home/Standard Search */}
                    <Route path="/" element={<StandardSearchPage />} />
                    <Route path="/search" element={<Navigate to="/" replace />} />

                    {/* Vendor Routes */}
                    <Route path="/vendor/verification" element={<VendorVerification />} />
                    <Route path="/vendor/verification/:uei" element={<VendorVerification />} />

                    {/* Investigator Routes */}
                    <Route path="/investigator" element={<Navigate to="/investigator/competition-analysis" replace />} />
                    <Route path="/investigator/vendor-verification" element={<Navigate to="/vendor/verification" replace />} />
                    <Route path="/investigator/deep-research-reports" element={<DeepResearchReports />} />
                    <Route path="/investigator/:type" element={<InvestigatorPage />} />

                    {/* Deep Research Routes */}
                    <Route path="/deep-research/prompts" element={<DeepResearchPrompts />} />

                    {/* Admin Routes */}
                    <Route
                      path="/admin/users"
                      element={
                        <ProtectedRoute requireAdmin>
                          <AdminUsersPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Fallback Route */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>

                  {/* Global Modal */}
                  <DetailModal />
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
    </AuthInitializer>
  );
};

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppContent />
      </ThemeProvider>
    </Provider>
  );
}

export default App;
